import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DropDown from "../../../molecules/DropDown";
import "./index.scss";
import { userSettings } from "../../../../constants/UserSettings";
import { useSelector } from "react-redux";
import UserCardAvatar from "../../../molecules/UserCardAvatar";

/* The menu is rendered into a portal and positioned against the trigger with a
   fixed strategy, so it floats over the page instead of being clipped by — or
   stretching — the navigation rail it opens from. `flip` and the viewport clamp
   in `calculateFloatingPosition` keep it on screen in the desktop rail (opens
   upward) and in the mobile drawer alike. */
const MENU_PLACEMENT = "top-start";
const MENU_OFFSET = { x: 0, y: 12 };
const MENU_VIEWPORT_GAP = 12;

/* Icon + label as the item's content. The icons are decorative — the label
   already names the action — so they stay out of the accessibility tree. */
const renderMenuItemLabel = ({ icon: Icon, label }) => (
    <>
        {Icon && <Icon className="nav-user-dropdown__icon" aria-hidden="true" focusable="false" />}
        <span className="nav-user-dropdown__label">{label}</span>
    </>
);

/**
 * @param portalRootId Element id to render the menu into. Callers inside an
 * `aria-modal` dialog must pass a host within that dialog, otherwise the menu
 * lands in the global `#portal` and assistive tech can treat it as unavailable.
 */
export function UserSettings({ portalRootId = undefined }) {
    const { user: currentUser } = useSelector((state) => state.auth);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const menuItems = useMemo(
        () => userSettings
            .filter((item) => (
                typeof item.featureEnabled === "function"
                    ? item.featureEnabled()
                    : item.featureEnabled !== false
            ))
            .map((item) => ({ ...item, label: t(item.labelKey) })),
        [t],
    );

    // Dropdown visibility
    const [visible, setVisible] = useState(false);
    const [activeComponent, setActiveComponent] = useState(null);

    // The trigger anchors the floating menu; the menu itself lives in the
    // portal, so outside-click detection has to test both nodes.
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const handleItemClick = (id) => {
        const item = menuItems.find((menuItem) => menuItem.id === id);
        setVisible(false); // Close the dropdown explicitly after handling the click

        if (!item) {
            return;
        }

        if (item.path) {
            setActiveComponent(null);
            navigate(item.path);
            return;
        }

        setActiveComponent(item);
    };

    // Function to close the Logout component
    const closePopupComponent = () => {
        setActiveComponent(null); // Clear the active item to close the Logout component
    };

    const closeMenu = useCallback(({ restoreFocus = false } = {}) => {
        setVisible(false);

        if (restoreFocus) {
            triggerRef.current?.focus();
        }
    }, []);

    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            const clickedTrigger = triggerRef.current?.contains(event.target);
            const clickedMenu = menuRef.current?.contains(event.target);

            if (!clickedTrigger && !clickedMenu) {
                closeMenu();
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                closeMenu({ restoreFocus: true });
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [closeMenu, visible]);

    const displayName = currentUser?.username || 'User';

    return (
        <>
            <nav>
                <div className="nav-user-container">
                    <button
                        type="button"
                        ref={triggerRef}
                        className="nav-user-trigger"
                        aria-haspopup="menu"
                        aria-expanded={visible}
                        onClick={() => setVisible((prev) => !prev)}
                    >
                        <UserCardAvatar
                            userId={currentUser?.id}
                            username={currentUser?.username}
                            className="user-settings-avatar-wrapper"
                            avatarId="nav-user-avatar-img"
                            radius="round"
                            enableProfilePreview={false}
                            defaultAvatarBackground
                        />
                        <span className="current-user-name">{displayName}</span>
                    </button>
                    {visible && (
                        <DropDown
                            dropdownRef={menuRef}
                            anchorRef={triggerRef}
                            items={menuItems}
                            onItemSelect={(item) => handleItemClick(item.id)}
                            getItemLabel={renderMenuItemLabel}
                            className="nav-user-dropdown"
                            placement={MENU_PLACEMENT}
                            offset={MENU_OFFSET}
                            gap={MENU_VIEWPORT_GAP}
                            strategy="fixed"
                            renderInPortal
                            portalRootId={portalRootId}
                        />
                    )}
                </div>
            </nav>
            {activeComponent && activeComponent.component && (
                <div>
                    <activeComponent.component onClose={closePopupComponent} />
                </div>
            )}
        </>
    );
}

UserSettings.propTypes = {
    portalRootId: PropTypes.string,
};
